import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { CAMPAIGN_SEARCH_PLACEHOLDER } from '@/utils/campaignSearch';
import { campaignAdProductLabel, type CampaignAdProduct } from '@/types/spCampaigns';
import { cn } from '@/utils/cn';
import {
  filterAutomationCampaigns,
  toggleAutomationCampaign,
  type AutomationCampaignOption,
} from './automationCampaignScope';

function adProductLabel(adProduct: string): string {
  if (adProduct === 'SP' || adProduct === 'SB' || adProduct === 'SD') {
    return campaignAdProductLabel(adProduct);
  }
  return adProduct;
}

export function AutomationCampaignPicker({
  options,
  selected,
  onChange,
  isLoading,
  searchPlaceholder,
  emptyLabel,
  loadingLabel,
  countNoun,
  conflictIds,
  conflictLabel = 'Has dayparting',
  showAdProduct = true,
}: {
  options: AutomationCampaignOption[];
  selected: AutomationCampaignOption[];
  onChange: (next: AutomationCampaignOption[]) => void;
  isLoading?: boolean;
  searchPlaceholder?: string;
  emptyLabel?: string;
  loadingLabel?: string;
  countNoun?: string;
  conflictIds?: Iterable<string>;
  conflictLabel?: string;
  showAdProduct?: boolean;
}) {
  const [search, setSearch] = useState('');
  const visible = useMemo(
    () => filterAutomationCampaigns(options, search),
    [options, search]
  );
  const selectedIds = useMemo(
    () => new Set(selected.map((c) => c.campaignId)),
    [selected]
  );
  const conflicts = useMemo(() => new Set(conflictIds ?? []), [conflictIds]);

  return (
    <div className="space-y-2 rounded border border-border p-3">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((c) => (
            <span
              key={c.campaignId}
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-surface-muted px-2 py-0.5 text-xs"
            >
              <span className="truncate">{c.name || c.campaignId}</span>
              <button
                type="button"
                className="text-foreground-muted hover:text-foreground"
                aria-label={`Remove ${c.name || c.campaignId}`}
                onClick={() => onChange(toggleAutomationCampaign(selected, c, false))}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={searchPlaceholder || CAMPAIGN_SEARCH_PLACEHOLDER}
        className="w-full rounded border border-border px-3 py-2 text-sm"
      />
      {isLoading ? (
        <p className="text-xs text-foreground-muted">{loadingLabel || 'Loading campaigns…'}</p>
      ) : visible.length === 0 ? (
        <p className="text-xs text-foreground-muted">{emptyLabel || 'No active campaigns match.'}</p>
      ) : (
        <ul className="m-0 max-h-40 list-none divide-y divide-border overflow-y-auto rounded border border-border bg-surface p-0">
          {visible.map((c) => (
            <li key={c.campaignId}>
              <label
                className={cn(
                  'flex cursor-pointer items-start gap-2 px-2.5 py-2 text-sm hover:bg-surface-muted'
                )}
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={selectedIds.has(c.campaignId)}
                  onChange={(e) =>
                    onChange(toggleAutomationCampaign(selected, c, e.target.checked))
                  }
                />
                <span className="min-w-0">
                  <span className="block truncate font-medium text-foreground">
                    {c.name || c.campaignId}
                  </span>
                  <span className="block truncate text-xs text-foreground-muted">
                    {showAdProduct ? `${adProductLabel(c.adProduct as CampaignAdProduct)} · ` : ''}
                    {c.parentLabel ? `${c.parentLabel} · ` : ''}
                    {c.campaignId}
                    {conflicts.has(c.campaignId) ? ` · ${conflictLabel}` : ''}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-foreground-muted">
        {selected.length} {countNoun || 'campaigns'} selected
      </p>
    </div>
  );
}
